import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { RightsContext } from 'providers/RightsContext';
import { Grid, Tooltip, Menu, MenuItem, MenuList, Icon } from '@mui/material';
import { Dialog, IconButton, Button, TextField, Autocomplete, Typography } from '@totalsoft/rocket-ui';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import { ApprovalEditabilityStates, DecisionStatus, DecisionBehavior } from '../constants';

const ApprovalLevelComponent = ({
  onPropertyChange,
  index,
  onSave,
  onApply,
  validationInfo,
  dirtyInfo,
  level,
  applying,
  t,
  decisionTypes,
  showApprovalHistory,
  setQueueSaveForIndex,
  lastApprovalIndex,
  userIsOnCurrentLevel,
  userIsOnPreviousLevel,
  userIsRequestedUser,
  decisionReasons,
  selectedOffer,
  anyOtherLevelHasReason
}) => {
  const isDisabled = level.decisionType.decision?.localeCompare(DecisionStatus.Conditional) !== 0 || !level.hasEditAccess;
  const { readOnlyFull, isImage, decisionApprovalLevelWrite } = useContext(RightsContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReasonModalClose = () => {
    setShowModal(false);
  };

  const handleReasonChange = value => {
    onPropertyChange(index, 'decisionReasonId', value.decisionReasonId);
    onPropertyChange(index, 'reason', value.reason);
    setQueueSaveForIndex(index);
    setShowModal(false);
  };

  const handleDecisionChange = value => {
    handleMenuClose();
    onPropertyChange(index, 'decisionType', value);
    if (
      level.decisionBehaviorCode?.localeCompare(DecisionBehavior.WithReason) === 0 ||
      (level.decisionBehaviorCode?.localeCompare(DecisionBehavior.OnlyOneReason) === 0 && !anyOtherLevelHasReason)
    ) {
      if (value?.decision?.localeCompare(DecisionStatus.Rejected) === 0) {
        setShowModal(true);
      } else {
        onPropertyChange(index, 'decisionReasonId', null);
        onPropertyChange(index, 'reason', null);
        setQueueSaveForIndex(index);
      }
    } else setQueueSaveForIndex(index);
  };

  const handleAdvancePercentChange = advancePercent => {
    advancePercent !== undefined && onPropertyChange(index, 'advancePercent', advancePercent);
  };

  const handlePeriodChange = durationMonths => {
    onPropertyChange(index, 'durationMonths', durationMonths);
  };

  const handleTextFieldChange = prop => value => {
    onPropertyChange(index, prop, value);
  };

  const handleNumberTextFieldChange = value => {
    onPropertyChange(index, 'residualValuePercent', value);
  };

  const handleSave = () => {
    onSave(index);
  };

  const handleApply = () => {
    onApply(index);
  };

  const isSaveDisabled = () => {
    return (
      readOnlyFull ||
      !decisionApprovalLevelWrite ||
      !validationInfo.isValid ||
      !dirtyInfo.isDirty ||
      level.applying ||
      level.saving ||
      applying
    );
  };

  const isApplyDisabled = () => {
    return (
      isDisabled ||
      readOnlyFull ||
      !decisionApprovalLevelWrite ||
      !validationInfo.isValid ||
      dirtyInfo.isDirty ||
      level.applying ||
      applying ||
      !selectedOffer ||
      (selectedOffer.advancePercent === level.advancePercent &&
        selectedOffer.duration === level.durationMonths &&
        selectedOffer.rezidualPercent === level.residualValuePercent)
    );
  };

  const shouldDisplayRequestedUserDropDown =
    level.approvalEditabilityState === ApprovalEditabilityStates.WaitingForNomination ||
    (index !== 0 && level.withNominalApproval && level.requestedApprovalUserId !== null && level.requestedApprovalUserId !== undefined);

  return (
    <Grid item container spacing={1}>
      <Grid item xs={12} sm={10}>
        <Typography variant='body2' fontWeight='bold'>
          {t('Document.Decision.ApprovalLevels.ApprovalLevelType') + ': ' + level.approvalLevelTypeName}
          {level.decisionType.decisionTypeId && level.decisionReasonId
            ? ' - ' +
              t('Document.Decision.ApprovalLevels.DecisionMadeByOnDateWithReason', {
                data: {
                  decision: level.decisionType.decisionTypeName || '',
                  personName: level.changedById ? t('By') + ' ' + level.changedByName : '',
                  time: t('DATE_FORMAT', { date: { value: level.decisionDate, format: 'DD MMMM YYYY HH:mm' } }),
                  reason: level.reason || 'The customer is not eligible according to the lending norms ongoing'
                }
              })
            : ' - ' +
              t('Document.Decision.ApprovalLevels.DecisionMadeByOnDate', {
                data: {
                  decision: level.decisionType.decisionTypeName || '',
                  personName: level.changedById ? t('By') + ' ' + level.changedByName : '',
                  time: t('DATE_FORMAT', { date: { value: level.decisionDate, format: 'DD MMMM YYYY HH:mm' } })
                }
              })}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={2} container justifyContent='flex-end'>
        {level.decisionType.decision && level.decisionType.decision?.localeCompare(DecisionStatus.Approved) === 0 && (
          <IconButton color='primary' variant='text'>
            <CheckCircle />
          </IconButton>
        )}
        {level.decisionType.decision && level.decisionType.decision?.localeCompare(DecisionStatus.Rejected) === 0 && (
          <IconButton color='primary' variant='text'>
            <Cancel />
          </IconButton>
        )}
      </Grid>
      {shouldDisplayRequestedUserDropDown && (
        <Grid item xs={12} sm={3}>
          <Tooltip title={!userIsOnPreviousLevel ? t('Document.Decision.ApprovalLevels.MustBeOnPreviousLevel') : ''}>
            <Autocomplete
              fullWidth
              labelKey='userName'
              valueKey='userId'
              value={level.requestedApprovalUserId}
              isSearchable={true}
              simpleValue={true}
              isClearable={false}
              onChange={value => onPropertyChange(index, 'requestedApprovalUserId', value)}
              label={t('Document.Decision.ApprovalLevels.RequestedApprovalUser')}
              options={level.usersEligibleToDecide}
              disabled={
                !userIsOnPreviousLevel ||
                level.decisionType.decision?.localeCompare(DecisionStatus.Approved) === 0 ||
                level.decisionType.decision?.localeCompare(DecisionStatus.Rejected) === 0
              }
            />
          </Tooltip>
        </Grid>
      )}
      <Grid item xs={12} sm={shouldDisplayRequestedUserDropDown ? 3 : 4}>
        <TextField
          disabled={
            readOnlyFull ||
            !decisionApprovalLevelWrite ||
            !level.hasEditAccess ||
            level.userHasAlreadyApproved ||
            level.approvalEditabilityState !== ApprovalEditabilityStates.Editable ||
            (level.withNominalApproval && !userIsOnCurrentLevel) ||
            (level.withNominalApproval && level.requestedApprovalUserId && !userIsRequestedUser)
          }
          fullWidth
          isNumeric
          value={level.advancePercent}
          name='advancePercent'
          label={t('Document.Decision.ApprovalLevels.AdvancePercent')}
          onChange={handleAdvancePercentChange}
          error={validationInfo && validationInfo.advancePercent ? true : false}
          helperText={validationInfo?.advancePercent}
        />
      </Grid>
      <Grid item xs={12} sm={shouldDisplayRequestedUserDropDown ? 3 : 4}>
        <TextField
          disabled={
            readOnlyFull ||
            !decisionApprovalLevelWrite ||
            !level.hasEditAccess ||
            level.userHasAlreadyApproved ||
            level.approvalEditabilityState !== ApprovalEditabilityStates.Editable ||
            (level.withNominalApproval && !userIsOnCurrentLevel) ||
            (level.withNominalApproval && level.requestedApprovalUserId && !userIsRequestedUser)
          }
          fullWidth
          isNumeric
          value={level.durationMonths}
          name='durationMonths'
          label={t('Document.Decision.ApprovalLevels.Period')}
          onChange={handlePeriodChange}
          inputProps={{ decimalScale: 0 }}
          error={validationInfo && validationInfo.durationMonths ? true : false}
          helperText={validationInfo?.durationMonths}
        />
      </Grid>
      <Grid item xs={12} sm={shouldDisplayRequestedUserDropDown ? 3 : 4}>
        <TextField
          disabled={
            readOnlyFull ||
            !decisionApprovalLevelWrite ||
            !level.hasEditAccess ||
            level.userHasAlreadyApproved ||
            level.approvalEditabilityState !== ApprovalEditabilityStates.Editable ||
            (level.withNominalApproval && !userIsOnCurrentLevel) ||
            (level.withNominalApproval && level.requestedApprovalUserId && !userIsRequestedUser)
          }
          fullWidth
          isNumeric
          value={level.residualValuePercent}
          onChange={handleNumberTextFieldChange}
          name='residualValuePercent'
          label={t('Document.Decision.ApprovalLevels.ResidualValuePercent')}
        />
      </Grid>
      <Grid item xs={12} sm={shouldDisplayRequestedUserDropDown ? 3 : 4}>
        <TextField
          disabled
          fullWidth
          isNumeric
          value={level.financingValuePercent}
          label={t('Document.Decision.ApprovalLevels.FinancingValuePercent')}
        />
      </Grid>
      <Grid item xs={12} sm={shouldDisplayRequestedUserDropDown ? 3 : 4}>
        <TextField disabled fullWidth isNumeric value={level.financingValue} label={t('Document.Decision.ApprovalLevels.FinancingValue')} />
      </Grid>
      <Grid item xs={12}>
        <TextField
          disabled={
            readOnlyFull ||
            !decisionApprovalLevelWrite ||
            !level.hasEditAccess ||
            level.userHasAlreadyApproved ||
            level.approvalEditabilityState !== ApprovalEditabilityStates.Editable ||
            (level.withNominalApproval && !userIsOnCurrentLevel) ||
            (level.withNominalApproval && level.requestedApprovalUserId && !userIsRequestedUser)
          }
          multiline
          fullWidth
          onChange={handleTextFieldChange('comment')}
          value={level.comment || ''}
          name='comment'
          label={t('Document.Decision.ApprovalLevels.Comment')}
          inputProps={{
            maxLength: 500
          }}
        />
      </Grid>
      <Grid container justifyContent='space-between' alignItems='center' mt={2} mx={1}>
        {!isImage && (
          <Grid item display='flex' gap={1}>
            <Button onClick={handleSave} disabled={isSaveDisabled()}>
              {level.saving ? t('Saving') + '...' : t('Save')}
            </Button>
            {!level.isSystem && (
              <Button onClick={handleApply} disabled={isApplyDisabled()}>
                {level.applying ? t('Applying') + '...' : t('Apply')}
              </Button>
            )}
            <Button
              id={'approvalLevel_decisionBtn'}
              aria-controls='decision-type-menu'
              aria-haspopup='true'
              tooltip={
                level.withNominalApproval && level.requestedApprovalUserId && !userIsRequestedUser
                  ? t('Document.Decision.ApprovalLevels.MustBeRequestedUser')
                  : ''
              }
              disabled={
                readOnlyFull ||
                !decisionApprovalLevelWrite ||
                !level.hasEditAccess ||
                level.applying ||
                level.saving ||
                level.userHasAlreadyApproved ||
                (level.approvalEditabilityState !== ApprovalEditabilityStates.Editable && !level.decisionType.decision) ||
                !userIsOnCurrentLevel ||
                (level.withNominalApproval && level.requestedApprovalUserId && !userIsRequestedUser)
              }
              onClick={handleMenuOpen}
            >
              {t('Document.Decision.ApprovalLevels.Decision')}
              <Icon fontSize='20px'>arrow_drop_down</Icon>
            </Button>
            <Menu id='decision-type-menu' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
              {decisionTypes.map(item => {
                return (
                  <MenuItem key={item.decisionTypeId} onClick={() => handleDecisionChange(item)}>
                    {item.decisionTypeName}
                  </MenuItem>
                );
              })}
            </Menu>
            <Dialog
              open={showModal}
              aria-labelledby='reasons-dialog-title'
              title={t('Document.Decision.ApprovalLevels.DecisionReasonTitle')}
              content={
                <MenuList>
                  {decisionReasons &&
                    decisionReasons.map(item => {
                      return (
                        <MenuItem key={item.decisionReasonId} onClick={() => handleReasonChange(item)}>
                          <Typography>{item.reason}</Typography>
                        </MenuItem>
                      );
                    })}
                </MenuList>
              }
              onClose={handleReasonModalClose}
            />
          </Grid>
        )}
        {index === lastApprovalIndex && (
          <Grid item>
            <IconButton
              color='primary'
              onClick={showApprovalHistory}
              tooltip={t('Document.Decision.ApprovalLevels.ApprovalsHistory.Title')}
              variant='text'
            >
              <HistoryIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

ApprovalLevelComponent.propTypes = {
  t: PropTypes.func.isRequired,
  decisionTypes: PropTypes.array.isRequired,
  applying: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  level: PropTypes.object.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  userIsOnCurrentLevel: PropTypes.bool.isRequired,
  userIsOnPreviousLevel: PropTypes.bool.isRequired,
  userIsRequestedUser: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  lastApprovalIndex: PropTypes.number,
  showApprovalHistory: PropTypes.func.isRequired,
  setQueueSaveForIndex: PropTypes.func.isRequired,
  decisionReasons: PropTypes.array,
  selectedOffer: PropTypes.object,
  anyOtherLevelHasReason: PropTypes.bool.isRequired
};

export default withTranslation()(ApprovalLevelComponent);
